import React, { useState, useEffect, useCallback } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import {
  Paper,
  Box,
  Checkbox,
  IconButton,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
  Tooltip,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import Cookie from "js-cookie";
import URLs from "constants/urls";
import {
  StyledTypography,
  StyledAgGridContainer,
  StyledButton,
  DeleteButton,
  StyledSnackbar,
  StyledAlert,
} from "components/StyledComponents/styledComponents.js";
import { subDialogContentStyle, dialogContentTextStyle } from "components/Styles/approvalStyles";
import VisibilityIcon from "@mui/icons-material/Visibility"; // View Icon
import CancelIcon from "@mui/icons-material/Cancel"; // Cancel Icon
import PropTypes from "prop-types";

export default function DSAInvoiceReview() {
  const [dsaInvoiceReceipt, setDsaInvoiceReceipt] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const jwt_token = Cookie.get("jwt_token");
  const [dsaDialogOpen, setDsaDialogOpen] = useState(false);
  const [dsaDialogAction, setDsaDialogAction] = useState(null);
  const [dsaDialogMessage, setDsaDialogMessage] = useState("");
  const [cancleInvoice, setCancleInvoice] = useState({});
  const [openCancelDialog, setOpenCancelDialog] = useState(false);

  const handleOpenDsaDialog = (action, message) => {
    setDsaDialogAction(() => action);
    setDsaDialogMessage(message);
    setDsaDialogOpen(true);
  };
  const handleCancelInvoiceDialog = () => {
    handleCancelInvoice(cancleInvoice);
  };
  const handleCloseCancelDialog = () => setOpenCancelDialog(false);

  const handleCloseDsaDialog = () => {
    setDsaDialogOpen(false);
    setDsaDialogAction(null);
    setDsaDialogMessage("");
  };
  const handleOpenCancelDialog = (data) => {
    setCancleInvoice(data);
    setOpenCancelDialog(true);
  };

  const handleConfirmDsaDialog = () => {
    if (dsaDialogAction) {
      dsaDialogAction();
    }
    handleCloseDsaDialog();
  };

  useEffect(() => {
    getDsaInvoicesReceipts();
  }, []);

  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const getDsaInvoicesReceipts = async () => {
    const url = `${URLs.headOfficeInvoiceCheck}`;
    setLoading(true);
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDsaInvoiceReceipt(data);
      } else {
        console.error("Failed to fetch DSA invoice receipts");
      }
    } catch (error) {
      console.error("Error fetching DSA invoice receipts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxClick = (invoiceNo) => {
    if (invoiceNo) {
      setSelectedInvoices((prev) =>
        prev.includes(invoiceNo) ? prev.filter((id) => id !== invoiceNo) : [...prev, invoiceNo]
      );
    }
  };

  const handleSelectAll = () => {
    const validInvoices = dsaInvoiceReceipt
      .map((row) => row.invoiceNo)
      .filter((invoice) => invoice !== null && invoice !== undefined); // Remove null/undefined values
    setSelectedInvoices(selectedInvoices.length === validInvoices.length ? [] : validInvoices);
  };

  const HeaderCheckbox = () => (
    <>
      <Checkbox
        checked={
          selectedInvoices.length === dsaInvoiceReceipt.length && dsaInvoiceReceipt.length > 0
        }
        indeterminate={
          selectedInvoices.length > 0 && selectedInvoices.length < dsaInvoiceReceipt.length
        }
        onChange={handleSelectAll}
      />
      <DeleteButton sx={{ fontWeight: "bold", borderRadius: "10px" }}>Select All</DeleteButton>
    </>
  );
  const handleCancelInvoice = async (invoiceData) => {
    const url = URLs.cancelInvoiceUrl;
    setLoading(true);
    try {
      const payload = [invoiceData.invoiceNo];
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
        body: JSON.stringify(payload),
      });

      // Handle plain text response correctly
      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || "Failed to Cancel Invoice");
      }
      getDsaInvoicesReceipts();
      setOpenCancelDialog(false);
      setAlert("Invoice Cancelled successfully!", "success");
    } catch (error) {
      setAlert(error.message, "error"); // ✅ Fix: Pass the error message
    }
    setLoading(false);
  };

  const handleApproveInvoices = async () => {
    const validInvoices = selectedInvoices.filter(
      (invoice) => invoice !== null && invoice !== undefined
    );

    if (validInvoices.length === 0) {
      setAlert("Please select at least one valid invoice to approve.");
      return;
    }

    const url = `${URLs.headOfficeApprovedMIS}`;

    try {
      setLoading(true);
      const invoiceData = {
        invoicesNo: validInvoices,
        bankID: 1,
      };
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
        body: JSON.stringify(invoiceData),
      });

      const responseText = await response.text();
      if (response.ok) {
        setAlert(`Invoices approved successfully`, "success");
        getDsaInvoicesReceipts();
      } else {
        setAlert(`Failed to approve invoices: ${responseText}`, "error");
      }
    } catch (error) {
      setAlert("An error occurred while approving invoices.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePrintInvoice = useCallback(async (invoiceNo) => {
    try {
      if (!invoiceNo) {
        throw new Error("Invoice number is missing");
      }

      const response = await fetch(`${URLs.headOfficeInvoiceView}/${invoiceNo}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jwt_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch invoice");
      }

      const blob = await response.blob();
      const pdfUrl = URL.createObjectURL(blob);

      // Open in a new tab
      window.open(pdfUrl, "_blank");
    } catch (error) {
      console.error("Error fetching invoice:", error);
    }
  }, []);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value ?? 0);

  // Utility function to format date as "DD-MMM-YYYY"
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return `${date.getDate()}-${date.toLocaleString("en-US", {
      month: "short",
    })}-${date.getFullYear()}`;
  };

  const columnDefs = [
    {
      headerName: "Actions",
      field: "actions",
      headerComponent: HeaderCheckbox,
      width: 150,
      cellRenderer: (params) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Checkbox
            checked={selectedInvoices.includes(params.data.invoiceNo)}
            onChange={() => handleCheckboxClick(params.data.invoiceNo)}
          />
          <Tooltip title="View Invoice" arrow>
            <IconButton onClick={() => handlePrintInvoice(params.data.invoiceNo)}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel Invoice">
            <IconButton onClick={() => handleOpenCancelDialog(params.data)} color="error">
              <CancelIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      headerName: "Date",
      field: "date",
      sortable: true,
      filter: true,
      flex: 1,
      valueFormatter: (params) => formatDate(params.value),
      sort: "desc",
    },
    {
      headerName: "Invoice Number",
      field: "invoiceNo",
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Amount",
      field: "amount",
      sortable: true,
      filter: true,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      headerName: "TDS (%)",
      field: "tdsPercent",
      sortable: true,
      filter: true,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "400" },
      valueFormatter: (params) => `${params.value ?? 0}%`,
    },
    {
      headerName: "TDS Amount",
      field: "tdsAmount",
      sortable: true,
      filter: true,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      headerName: "Net DSA Amount",
      field: "netDsaAmount",
      sortable: true,
      filter: true,
      flex: 1,
      cellStyle: { textAlign: "right", fontWeight: "500" },
      valueFormatter: (params) => formatCurrency(params.value),
    },
    {
      headerName: "DSA Code",
      field: "dsaCode",
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "DSA Name",
      field: "dsaName",
      sortable: true,
      filter: true,
      flex: 1,
    },
  ];
  const ConfirmationDialog = ({ open, onClose, onConfirm, dialogText }) => {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogContent sx={{ mt: 1, ...subDialogContentStyle }}>
          <DialogContentText sx={dialogContentTextStyle}>{dialogText}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <StyledButton onClick={onClose} color="secondary">
            Cancel
          </StyledButton>
          <StyledButton onClick={onConfirm} color="primary" autoFocus>
            Confirm
          </StyledButton>
        </DialogActions>
      </Dialog>
    );
  };

  ConfirmationDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    dialogText: PropTypes.string.isRequired,
  };

  return (
    <DashboardLayout>
      <ConfirmationDialog
        open={openCancelDialog}
        onClose={handleCloseCancelDialog}
        onConfirm={handleCancelInvoiceDialog}
        dialogText={`Are you sure you want to cancel this invoice of Invoice NO ${cancleInvoice.invoiceNo}?`}
      />
      <Dialog
        open={loading}
        onClose={() => setLoading(false)}
        aria-labelledby="loading-dialog-title"
      >
        <DialogContent style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="primary" />
          <Typography variant="h6" style={{ marginTop: "20px" }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>
      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        severity={alertType}
      >
        <StyledAlert onClose={handleClose} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>
      <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
        <Box sx={{ flexGrow: 1, mt: 2 }}>
          <StyledAgGridContainer component={Paper} className="ag-theme-alpine">
            <AgGridReact
              rowData={dsaInvoiceReceipt}
              columnDefs={columnDefs}
              pagination={true}
              paginationPageSize={10}
            />
          </StyledAgGridContainer>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", m: 2 }}>
          <StyledButton
            onClick={() =>
              handleOpenDsaDialog(
                handleApproveInvoices,
                "Are you sure you want to Approve Invoices?"
              )
            }
            disabled={loading}
          >
            Approve
          </StyledButton>
        </Box>
        <Dialog open={dsaDialogOpen} onClose={handleCloseDsaDialog}>
          <DialogContent sx={{ mt: 1, ...subDialogContentStyle }}>
            {dsaDialogMessage || "Are you sure you want to proceed with this action?"}
          </DialogContent>
          <DialogActions>
            <StyledButton onClick={handleCloseDsaDialog} color="secondary">
              Cancel
            </StyledButton>
            <StyledButton onClick={handleConfirmDsaDialog} color="primary">
              Confirm
            </StyledButton>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
}
